/**
 * DL-01 — the "Download images" action: a text button (a dialog's actions, a
 * detail) or an icon button (a table cell), hidden entirely when the caller
 * is not eligible (`eligibility.ts`) — never a disabled control. Opens the
 * dialog for its scope.
 */
import { useState, type MouseEvent, type ReactElement } from "react";
import { Button, IconButton, Tooltip } from "@mui/material";
import { DownloadIcon } from "@aeriosiq/ui/icons";
import { DownloadImagesDialog } from "./DownloadImagesDialog.js";
import type { DownloadScope } from "./manifestApi.js";
import { WORDS } from "./words.js";

export interface DownloadImagesButtonProps {
  scope: DownloadScope;
  /** Decided by the caller (`canDownloadImages`); false renders nothing. */
  eligible: boolean;
  variant?: "text" | "outlined" | "icon" | undefined;
  size?: "small" | "medium" | undefined;
  /** The words on a text button (default "Download images"). */
  label?: string | undefined;
}

export function DownloadImagesButton({
  scope,
  eligible,
  variant = "text",
  size = "small",
  label,
}: DownloadImagesButtonProps): ReactElement | null {
  const [open, setOpen] = useState(false);
  if (!eligible) return null;
  const words = label ?? (scope.kind === "da" ? WORDS.action : WORDS.actionAll);
  const testId = `dl-open-${scope.kind}-${scope.id}`;
  const onClick = (e: MouseEvent<HTMLElement>): void => {
    // a table row's own click (open the analysis) must not fire
    e.stopPropagation();
    setOpen(true);
  };
  return (
    <>
      {variant === "icon" ? (
        <Tooltip title={words}>
          <IconButton
            size={size}
            onClick={onClick}
            aria-label={words}
            data-testid={testId}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          size={size}
          variant={variant}
          startIcon={<DownloadIcon fontSize="small" />}
          onClick={onClick}
          data-testid={testId}
        >
          {words}
        </Button>
      )}
      <DownloadImagesDialog
        scope={open ? scope : null}
        onClose={() => {
          setOpen(false);
        }}
      />
    </>
  );
}
